import { Bar, CartesianGrid, ComposedChart, Legend, Line, Tooltip, XAxis, YAxis } from 'recharts'
import ChartContainer from './ChartContainer'
import { RechartsChartTooltipProvider, StableRechartsChartTooltip } from './ChartTooltip'
import { defaultFormatter } from './formatters'
import { addTrendToData } from '../dashboard/utils/trendline'

const TREND_WINDOW = 7

const BaseBarChart = ({
  data,
  xKey,
  series,
  height = 240,
  loading,
  emptyMessage,
  valueFormatter,
  labelFormatter,
  layout = 'horizontal',
  showLegend = true,
  tooltipExtra,
  xTickFormatter,
  yTickFormatter,
  xTickAngle = 0,
  xAxisHeight,
  yAxisWidth,
  yAxisLabel,
  rightYAxisLabel,
  barSize,
  barCategoryGap,
  barGap,
  showTrend = true,
}) => {
  const empty = !data || data.length === 0
  const isVertical = layout === 'vertical'

  const hasStacked = (series || []).some((s) => s.stackId)
  const seriesKeysForTrend = hasStacked
    ? [(series || [])[0]?.key].filter(Boolean)
    : (series || []).map((s) => s.key).filter(Boolean)
  const dataWithTrend =
    showTrend && seriesKeysForTrend.length > 0 && !isVertical
      ? addTrendToData(data, seriesKeysForTrend, Math.min(TREND_WINDOW, Math.max(1, data.length - 1)))
      : data

  const normalizedSeries = (series || []).map((item) => ({
    ...item,
    formatter: item.formatter || valueFormatter || defaultFormatter,
  }))

  const usesDualYAxis =
    !isVertical && normalizedSeries.some((item) => item.yAxisId === 'right')

  const trendSeries = showTrend && !isVertical
    ? seriesKeysForTrend.map((key) => ({
        key: `${key}Trend`,
        label: `Trend (${TREND_WINDOW}-day avg)`,
        colorVar: normalizedSeries.find((s) => s.key === key)?.colorVar || '--chart-1',
        formatter: normalizedSeries.find((s) => s.key === key)?.formatter || defaultFormatter,
        yAxisId: normalizedSeries.find((s) => s.key === key)?.yAxisId,
      }))
    : []

  const allSeriesForTooltip = [...normalizedSeries, ...trendSeries]

  const leftAxisWidth =
    yAxisWidth || (isVertical ? 80 : usesDualYAxis ? 48 : undefined)
  const rightAxisWidth = yAxisWidth || (usesDualYAxis ? 48 : 44)

  // Place titles outside the tick band so they do not collide with values.
  const numericAxisLabel = (value, side = 'left') =>
    value
      ? {
          value,
          angle: -90,
          position: side === 'right' ? 'right' : 'left',
          offset: 12,
          fill: 'var(--chart-muted)',
          fontSize: 10,
          style: { textAnchor: 'middle' },
        }
      : undefined

  return (
    <ChartContainer height={height} loading={loading} empty={empty} emptyMessage={emptyMessage}>
      <RechartsChartTooltipProvider
        series={allSeriesForTooltip}
        options={{
          valueFormatter,
          labelFormatter,
          extraContent: tooltipExtra,
        }}
      >
      <ComposedChart
        data={dataWithTrend}
        layout={layout}
        margin={{
          top: 10,
          right: usesDualYAxis ? (rightYAxisLabel ? 28 : 20) : 20,
          left: usesDualYAxis && yAxisLabel ? 20 : 0,
          bottom: 10,
        }}
        barSize={barSize}
        barCategoryGap={barCategoryGap}
        barGap={barGap}
      >
        <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" />
        <XAxis
          type={isVertical ? 'number' : 'category'}
          dataKey={isVertical ? undefined : xKey}
          stroke="var(--chart-muted)"
          fontSize={11}
          tickLine={false}
          axisLine={{ stroke: 'var(--chart-border)' }}
          minTickGap={20}
          interval="preserveStartEnd"
          tickFormatter={xTickFormatter}
          angle={xTickAngle}
          textAnchor={xTickAngle ? 'end' : 'middle'}
          height={xAxisHeight}
        />
        <YAxis
          yAxisId={usesDualYAxis ? 'left' : undefined}
          type={isVertical ? 'category' : 'number'}
          dataKey={isVertical ? xKey : undefined}
          stroke="var(--chart-muted)"
          fontSize={11}
          tickLine={false}
          axisLine={{ stroke: 'var(--chart-border)' }}
          width={leftAxisWidth}
          tickFormatter={yTickFormatter}
          label={numericAxisLabel(yAxisLabel, 'left')}
        />
        {usesDualYAxis && (
          <YAxis
            yAxisId="right"
            orientation="right"
            type="number"
            stroke="var(--chart-muted)"
            fontSize={11}
            tickLine={false}
            axisLine={{ stroke: 'var(--chart-border)' }}
            width={rightAxisWidth}
            tickFormatter={yTickFormatter}
            label={numericAxisLabel(rightYAxisLabel, 'right')}
          />
        )}
        <Tooltip content={StableRechartsChartTooltip} />
        {showLegend && <Legend verticalAlign="top" align="right" iconType="circle" className="rg-chart__legend" />}
        {normalizedSeries.map((item) => (
          <Bar
            key={item.key}
            dataKey={item.key}
            fill={`var(${item.colorVar})`}
            radius={isVertical ? [0, 4, 4, 0] : [4, 4, 0, 0]}
            name={item.label}
            stackId={item.stackId}
            yAxisId={usesDualYAxis ? (item.yAxisId || 'left') : undefined}
          />
        ))}
        {showTrend && !isVertical && trendSeries.map((item) => (
          <Line
            key={item.key}
            type="monotone"
            dataKey={item.key}
            name={item.label}
            stroke={`var(${item.colorVar})`}
            strokeWidth={2}
            strokeOpacity={0.6}
            dot={false}
            connectNulls
            yAxisId={usesDualYAxis ? (item.yAxisId || 'left') : undefined}
          />
        ))}
      </ComposedChart>
      </RechartsChartTooltipProvider>
    </ChartContainer>
  )
}

export default BaseBarChart
